/**
 * Blood Moon phase graph — matches BLOOD_MOON_FLOW.md, with the night ritual
 * pacing beats (NIGHT_INTRO / NIGHT_INTERLUDE) layered in:
 *
 *   LOBBY → DEAL → NIGHT_INTRO → NIGHT_WOLVES → NIGHT_INTERLUDE → NIGHT_SEER
 *         → RESOLVE_NIGHT → (END | DAWN)
 *   DAWN → DISCUSS → VOTE → RESOLVE_VOTE → (END | NIGHT_INTRO)
 *
 * NIGHT_INTRO is the ~10s "everyone sleeps, night music" pause before the wolves
 * wake; NIGHT_INTERLUDE is the ~8s pause where the wolves close their eyes (the
 * WOLVES_SLEEP beat) before the seer is summoned. Both are timed rest phases (no
 * player actions) that advance on their timer OR a host skip.
 *
 * Win is checked after RESOLVE_NIGHT and after RESOLVE_VOTE. Either resolve phase
 * can therefore branch to END. DAWN itself is a first-class ending path when the
 * night kill produces a wolves-win (see LEGAL_TRANSITIONS / RESOLVE_NIGHT → END).
 */
export const PHASES = [
    'LOBBY',
    'DEAL',
    'NIGHT_INTRO',
    'NIGHT_WOLVES',
    'NIGHT_INTERLUDE',
    'NIGHT_SEER',
    'RESOLVE_NIGHT',
    'DAWN',
    'DISCUSS',
    'VOTE',
    'RESOLVE_VOTE',
    // ── The Whisper (second game — see THE_WHISPER_SPEC.md §4) ──────────────────
    // WHISPER_INTRO (ritual) → QUESTION (candle) → RESOLVE_GUESS (transient) →
    // HUNT (vote) → RESOLVE_HUNT (reveal) → SCORE → (WHISPER_INTRO | END).
    // Inert for Blood Moon — these are never entered by the Blood Moon engine.
    'WHISPER_INTRO',
    'QUESTION',
    'RESOLVE_GUESS',
    'HUNT',
    'RESOLVE_HUNT',
    'SCORE',
    // ── Blood Moon: One Night (third game) ─────────────────────────────────────
    // ONE night, no elimination, then a single simultaneous vote:
    //   ONENIGHT_NIGHTFALL (ritual) → ONENIGHT_NIGHT_ACTIONS (EVERY night role —
    //   Werewolves, Seer, Thief AND Joker — acts in ONE timed beat, in parallel)
    //   → ONENIGHT_DAWN → ONENIGHT_DISCUSS → ONENIGHT_VOTE → ONENIGHT_RESOLVE →
    //   END.
    // Every night beat runs for EVERY table regardless of whether that role is in
    // play — a phase that is skipped when its role is absent would leak the setup
    // (an instant skip tells the table that role is in the centre).
    // ONENIGHT_NIGHT_ACTIONS is the seam for EVERY future night role: submission
    // order is irrelevant because swaps are recorded as intents during the beat
    // and applied at its end in the canonical ONUW card order (Thief before
    // Joker) — see NIGHT_SWAP_ORDER in engine/src/oneNight.ts. A new night role
    // joins THIS phase via the role→action-kind map rather than growing the graph.
    // Inert for Blood Moon and The Whisper — never entered by their engines.
    'ONENIGHT_NIGHTFALL',
    'ONENIGHT_NIGHT_ACTIONS',
    'ONENIGHT_DAWN',
    'ONENIGHT_DISCUSS',
    'ONENIGHT_VOTE',
    'ONENIGHT_RESOLVE',
    'END',
];
/** Enum-style constant object for ergonomic references (`Phases.NIGHT_WOLVES`). */
export const Phases = {
    LOBBY: 'LOBBY',
    DEAL: 'DEAL',
    NIGHT_INTRO: 'NIGHT_INTRO',
    NIGHT_WOLVES: 'NIGHT_WOLVES',
    NIGHT_INTERLUDE: 'NIGHT_INTERLUDE',
    NIGHT_SEER: 'NIGHT_SEER',
    RESOLVE_NIGHT: 'RESOLVE_NIGHT',
    DAWN: 'DAWN',
    DISCUSS: 'DISCUSS',
    VOTE: 'VOTE',
    RESOLVE_VOTE: 'RESOLVE_VOTE',
    // The Whisper phases.
    WHISPER_INTRO: 'WHISPER_INTRO',
    QUESTION: 'QUESTION',
    RESOLVE_GUESS: 'RESOLVE_GUESS',
    HUNT: 'HUNT',
    RESOLVE_HUNT: 'RESOLVE_HUNT',
    SCORE: 'SCORE',
    // Blood Moon: One Night phases.
    ONENIGHT_NIGHTFALL: 'ONENIGHT_NIGHTFALL',
    ONENIGHT_NIGHT_ACTIONS: 'ONENIGHT_NIGHT_ACTIONS',
    ONENIGHT_DAWN: 'ONENIGHT_DAWN',
    ONENIGHT_DISCUSS: 'ONENIGHT_DISCUSS',
    ONENIGHT_VOTE: 'ONENIGHT_VOTE',
    ONENIGHT_RESOLVE: 'ONENIGHT_RESOLVE',
    END: 'END',
};
/**
 * Legal forward transitions. The engine is the authority; this table documents
 * intent and lets tests assert no illegal jumps. Branch phases list every
 * legal successor.
 *
 * Notable branches (per plan Gameplay tweak #1 — DAWN can end the game directly
 * via the night kill, so RESOLVE_NIGHT may go straight to END):
 *   - RESOLVE_NIGHT → END | DAWN
 *   - RESOLVE_VOTE  → END | NIGHT_WOLVES
 */
export const LEGAL_TRANSITIONS = {
    LOBBY: ['DEAL'],
    // DEAL is the one shared phase all three games pass through; it branches to
    // whichever game's opening ritual follows. (WHISPER_INTRO was missing here —
    // the Whisper engine has always made that jump; the table had drifted.)
    DEAL: ['NIGHT_INTRO', 'WHISPER_INTRO', 'ONENIGHT_NIGHTFALL'],
    NIGHT_INTRO: ['NIGHT_WOLVES'],
    NIGHT_WOLVES: ['NIGHT_INTERLUDE'],
    NIGHT_INTERLUDE: ['NIGHT_SEER'],
    NIGHT_SEER: ['RESOLVE_NIGHT'],
    RESOLVE_NIGHT: ['END', 'DAWN'],
    DAWN: ['DISCUSS'],
    DISCUSS: ['VOTE'],
    VOTE: ['RESOLVE_VOTE'],
    RESOLVE_VOTE: ['END', 'NIGHT_INTRO'],
    // ── The Whisper ────────────────────────────────────────────────────────────
    // Linear round: intro → question → resolve-guess → hunt → resolve-hunt →
    // score, then loop to the next round's intro or end after N rounds.
    WHISPER_INTRO: ['QUESTION'],
    QUESTION: ['RESOLVE_GUESS'],
    RESOLVE_GUESS: ['HUNT'],
    HUNT: ['RESOLVE_HUNT'],
    RESOLVE_HUNT: ['SCORE'],
    SCORE: ['WHISPER_INTRO', 'END'],
    // ── Blood Moon: One Night ──────────────────────────────────────────────────
    // Strictly linear — no branch, no loop, no early END. There is exactly one
    // night and exactly one vote, so the only ending is through ONENIGHT_RESOLVE.
    ONENIGHT_NIGHTFALL: ['ONENIGHT_NIGHT_ACTIONS'],
    ONENIGHT_NIGHT_ACTIONS: ['ONENIGHT_DAWN'],
    ONENIGHT_DAWN: ['ONENIGHT_DISCUSS'],
    ONENIGHT_DISCUSS: ['ONENIGHT_VOTE'],
    ONENIGHT_VOTE: ['ONENIGHT_RESOLVE'],
    ONENIGHT_RESOLVE: ['END'],
    END: [],
};
/** True when `to` is a documented legal successor of `from`. */
export function isLegalTransition(from, to) {
    return LEGAL_TRANSITIONS[from].includes(to);
}
/**
 * Phases in which a player-facing timer runs (drives the Stage/Hand countdown).
 * NIGHT_INTRO/NIGHT_INTERLUDE are timed rest beats (the ambient-night pauses);
 * DAWN and RESOLVE_VOTE now carry a rest timer so the game never stalls waiting
 * on a host advance.
 */
export const TIMED_PHASES = [
    'DEAL',
    'NIGHT_INTRO',
    'NIGHT_WOLVES',
    'NIGHT_INTERLUDE',
    'NIGHT_SEER',
    'DAWN',
    'DISCUSS',
    'VOTE',
    'RESOLVE_VOTE',
    // The Whisper timed phases. RESOLVE_GUESS is transient (no timer).
    'WHISPER_INTRO',
    'QUESTION',
    'HUNT',
    'RESOLVE_HUNT',
    'SCORE',
    // Blood Moon: One Night — every phase after DEAL is timed. ONENIGHT_RESOLVE
    // carries a rest timer (the group reveal needs to breathe) and is the only
    // one that is not a player-action beat.
    'ONENIGHT_NIGHTFALL',
    'ONENIGHT_NIGHT_ACTIONS',
    'ONENIGHT_DAWN',
    'ONENIGHT_DISCUSS',
    'ONENIGHT_VOTE',
    'ONENIGHT_RESOLVE',
];
export function isTimedPhase(phase) {
    return TIMED_PHASES.includes(phase);
}
/**
 * Night sub-phases — the whole nightfall ritual. Includes the two ambient pause
 * beats (INTRO/INTERLUDE) plus the two acting beats (WOLVES/SEER). Used for
 * night theming / audio triggers.
 */
export const NIGHT_PHASES = [
    'NIGHT_INTRO',
    'NIGHT_WOLVES',
    'NIGHT_INTERLUDE',
    'NIGHT_SEER',
    // One Night's whole night — same theming/audio treatment as Blood Moon's.
    'ONENIGHT_NIGHTFALL',
    'ONENIGHT_NIGHT_ACTIONS',
];
export function isNightPhase(phase) {
    return NIGHT_PHASES.includes(phase);
}
//# sourceMappingURL=phases.js.map